import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentAssociationType } from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';
import {
  useUploadEntityDocument,
  useRemoveEntityDocument,
} from '@/api/rpc/hooks/core/useEntityDocuments';
import {
  useGetDiscountDocuments,
  DISCOUNT_DOCUMENTS_QUERY_KEY,
} from './useGetDiscountDocuments';

const ENTITY_TYPE = 'ar_discount';

/**
 * Shared hook for discount document operations (fetch, upload, remove, preview).
 * Used by both DiscountDetailModal and DiscountBadge.
 */
export function useDiscountDocuments(discountId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: documentsData } = useGetDiscountDocuments(discountId);

  const uploadDocument = useUploadEntityDocument();
  const removeDocument = useRemoveEntityDocument();

  const invalidateDocuments = useCallback(() => {
    if (!discountId) return;
    queryClient.invalidateQueries({
      queryKey: [DISCOUNT_DOCUMENTS_QUERY_KEY, discountId],
    });
  }, [discountId, queryClient]);

  const handleUploadFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      // Materialize the FileList before resetting e.target.value — resetting
      // empties the live FileList in place, which would otherwise leave us
      // iterating nothing.
      const selected = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (selected.length === 0 || !discountId) return;

      let uploadedAny = false;
      for (const file of selected) {
        try {
          await uploadDocument.mutateAsync({
            entityType: ENTITY_TYPE,
            entityId: discountId,
            file,
            associationType: DocumentAssociationType.DISCOUNT_ATTACHMENT,
          });
          uploadedAny = true;
        } catch {
          // Continue uploading remaining files even if one fails
        }
      }
      if (uploadedAny) {
        invalidateDocuments();
      }
    },
    [discountId, uploadDocument, invalidateDocuments],
  );

  const handleRemoveDocument = useCallback(
    async (documentId: string) => {
      if (!discountId) return;
      await removeDocument.mutateAsync(
        {
          documentId,
          entityType: ENTITY_TYPE,
          entityId: discountId,
        },
        { onSuccess: invalidateDocuments },
      );
    },
    [discountId, removeDocument, invalidateDocuments],
  );

  const getDocumentPreviewUrl = useCallback(async (documentId: string) => {
    const preview = await rpcClient.core.documents.getPreview({ documentId });
    return preview.signedUrl;
  }, []);

  return {
    documents: documentsData?.documents ?? [],
    isUploading: uploadDocument.isPending,
    handleUploadFile,
    handleRemoveDocument,
    getDocumentPreviewUrl,
  };
}
